<template>
  <main class="backgroundImage flex justify-center items-center h-screen">
    <div
      class="relative bg-white w-full xl:w-5/6 h-full xl:h-[85vh] max-xl:py-5 rounded-xl"
    >
      <div
        v-if="!success && !isLoading"
        class="relative flex flex-col w-full h-full"
      >
        <!-- STEPS -->
        <section class="grid grid-cols-3 bg-grey w-full rounded-t-xl">
          <button
            @click="goBackToSpecificStep(step.step)"
            v-for="step in steps"
            :key="step.step"
            :disabled="step.step > currentStep"
            class="text-center py-2 sm:py-3 md:py-5 disabled:cursor-not-allowed inabled:cursor-allowed"
            :class="currentStep === step.step ? 'bg-gold rounded-xl' : ''"
          >
            {{ step.step }}.
            <span class="max-sm:hidden">
              {{ lang === "mn" ? step.title_mn : step.title_en }}
            </span>
          </button>
        </section>

        <section class="flex-1 p-5 overflow-y-scroll">
          <YellowRegistrationStepOne
            v-if="currentStep === 1"
            ref="stepOneRef"
            :registrant="registrant"
          />

          <div v-else-if="currentStep === 2">
            <div class="flex justify-center my-5">
              <div class="flex bg-grey rounded-full">
                <button
                  @click="selectType(1)"
                  class="w-[8rem] sm:w-[15rem] p-1 sm:p-3 max-sm:text-xs uppercase"
                  :class="
                    selectedType === 1 ? 'bg-coal rounded-full text-white' : ''
                  "
                >
                  {{ lang === "mn" ? "1. Байгууллага" : "1. Company" }}
                </button>
                <button
                  @click="selectType(2)"
                  class="w-[10rem] sm:w-[15rem] p-1 sm:p-3 max-sm:text-sm uppercase"
                  :class="
                    selectedType === 2 ? 'bg-coal rounded-full text-white' : ''
                  "
                >
                  {{
                    lang === "mn" ? "2. Зөвлөх инженер" : "2.Mining consultant"
                  }}
                </button>
              </div>
            </div>

            <YellowCompanyRegister
              v-if="selectedType === 1"
              ref="companyRef"
              :company="company"
            />
            <YellowConsultantRegister
              v-else-if="selectedType === 2"
              ref="consultantRef"
              :consultant="consultant"
            />
          </div>

          <YellowRegistrationStepThree
            v-else-if="currentStep === 3"
            ref="stepThreeRef"
          />
        </section>

        <!-- BUTTONS -->
        <div
          class="absolute bottom-0 flex justify-between items-center w-full rounded-b-xl p-2 md:p-3 text-sm bg-white shadow-[0_-4px_10px_-4px_rgba(0,0,0,0.3)]"
        >
          <button
            v-if="currentStep !== 1"
            @click="backToPreviousStep()"
            class="text-gray-500 rounded hover:text-coal"
          >
            <font-awesome-icon icon="fa-solid fa-angles-left" class="mr-1" />
            {{ lang === "mn" ? "Буцах" : "Back" }}
          </button>
          <span v-else></span>
          <button
            v-if="currentStep !== 3"
            @click="addStep()"
            class="bg-gold text-black p-2 rounded group"
          >
            {{ lang === "mn" ? "Үргэлжлүүлэх" : "Next" }}
            <font-awesome-icon
              icon="fa-solid fa-angles-right"
              class="ml-1 group-hover:animate-bounceX"
            />
          </button>

          <button
            v-else
            @click="submitRequest()"
            class="bg-gold text-black p-2 rounded group"
          >
            <font-awesome-icon
              icon="fa-solid fa-paper-plane"
              class="mr-1 transition-transform duration-300 group-hover:rotate-45"
            />
            {{ lang === "mn" ? "Хүсэлт илгээх" : "Submit" }}
          </button>
        </div>
      </div>

      <div
        v-else-if="success && !isLoading"
        class="flex justify-center items-center w-full h-full"
      >
        <YellowRegistrationSuccessful @startAgain="startAgain" />
      </div>

      <div
        v-else-if="isLoading"
        class="flex justify-center items-center w-full h-full"
      >
        <YPLoading />
      </div>
    </div>
  </main>

  <ErrorModal v-if="error" :error="error" />
</template>

<script>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import clientYellowInstance from "@/lib/yellowClientInstance";
import YellowRegistrationStepOne from "@/containers/YellowPage/YPRegistrationSteps/YellowRegistrationStepOne.vue";
import YellowCompanyRegister from "@/containers/YellowPage/YPRegistrationSteps/YellowCompanyRegister.vue";
import YellowConsultantRegister from "@/containers/YellowPage/YPRegistrationSteps/YellowConsultantRegister.vue";
import YellowRegistrationStepThree from "@/containers/YellowPage/YPRegistrationSteps/YellowRegistrationStepThree.vue";
import YellowRegistrationSuccessful from "@/containers/YellowPage/YPRegistrationSteps/YellowRegistrationSuccessful.vue";
import ErrorModal from "@/components/ui/ErrorModal.vue";
import YPLoading from "@/components/YellowPage/YPLoading.vue";

export default {
  name: "YPRegistration",
  components: {
    YellowRegistrationStepOne,
    YellowCompanyRegister,
    YellowConsultantRegister,
    YellowRegistrationStepThree,
    YellowRegistrationSuccessful,
    ErrorModal,
    YPLoading,
  },
  setup() {
    const store = useStore();
    const lang = computed(() => store.getters.getLanguage);

    const success = ref(false);
    const error = ref(null);
    const isLoading = ref(false);

    const steps = ref([
      {
        step: 1,
        title_mn: "Бүртгүүлэгчийн мэдээлэл",
        title_en: "Registrant Information",
      },
      {
        step: 2,
        title_mn: "Байгууллагын мэдээлэл",
        title_en: "Organization Information",
      },
      {
        step: 3,
        title_mn: "Үйлчилгээний нөхцөл",
        title_en: "Terms & Conditions",
      },
    ]);
    const currentStep = ref(1);
    const selectedType = ref(1);
    const stepOneRef = ref(null);
    const companyRef = ref(null);
    const consultantRef = ref(null);
    const stepThreeRef = ref(null);

    const registrant = ref(null);
    const company = ref(null);
    const consultant = ref(null);

    const addStep = () => {
      if (currentStep.value === 1) {
        const isValid = stepOneRef.value?.validateForm?.();
        if (!isValid) return;

        registrant.value = { ...stepOneRef.value.form };
      } else if (currentStep.value === 2) {
        if (companyRef.value) {
          const isValid = companyRef.value?.validateForm?.();
          if (!isValid) return;

          company.value = {
            ...companyRef.value.form,
            mainImage: companyRef.value.mainImage,
          };
        }

        if (consultantRef.value) {
          const isValid = consultantRef.value?.validateForm?.();
          if (!isValid) return;

          consultant.value = { ...consultantRef.value.form };
        }
      }

      currentStep.value++;
    };

    const backToPreviousStep = () => {
      if (currentStep.value > 1) currentStep.value--;
    };

    const goBackToSpecificStep = (step) => {
      currentStep.value = step;
    };

    const startAgain = () => {
      registrant.value = null;
      company.value = null;
      consultant.value = null;
      success.value = false;
      isLoading.value = false;
      currentStep.value = 1;
    };

    const selectType = (item) => {
      if (item === 1) {
        consultant.value = null;
      } else {
        company.value = null;
      }

      selectedType.value = item;
    };

    const submitRequest = async () => {
      if (currentStep.value === 3) {
        const isValid = stepThreeRef.value?.validateForm?.();
        if (!isValid) return;
      }

      isLoading.value = true;

      try {
        let res;

        if (company.value) {
          company.value.nameMn = company.value.nameMn?.toUpperCase();
          company.value.nameEn = company.value.nameEn?.toUpperCase();

          const formData = new FormData();
          formData.append(
            "categories",
            JSON.stringify(company.value.categories)
          );
          formData.append("image", company.value.mainImage);
          formData.append("nameMn", company.value.nameMn);
          formData.append("nameEn", company.value.nameEn);
          formData.append(
            "registrationNumber",
            company.value.registrationNumber
          );
          formData.append("emails", company.value.emails);
          formData.append("phones", company.value.phones);
          formData.append("faxes", company.value.faxes);
          formData.append("websites", company.value.websites);
          formData.append("addressMn", company.value.addressMn);
          formData.append("addressEn", company.value.addressEn);
          formData.append("aboutMn", company.value.aboutMn);
          formData.append("aboutEn", company.value.aboutEn);
          formData.append("registrantName", registrant.value.name ?? null);
          formData.append(
            "registrantPosition",
            registrant.value.position ?? null
          );
          formData.append("registrantEmail", registrant.value.email ?? null);
          formData.append("registrantPhone", registrant.value.phone ?? null);

          res = await clientYellowInstance.post(
            "/insert/yellow-registration",
            formData
          );
        } else if (consultant.value) {
          consultant.value.nameMn = consultant.value.nameMn?.toUpperCase();
          consultant.value.nameEn = consultant.value.nameEn?.toUpperCase();

          consultant.value.registrantName = registrant.value.name;
          consultant.value.registrantPosition = registrant.value.position;
          consultant.value.registrantEmail = registrant.value.email;
          consultant.value.registrantPhone = registrant.value.phone;

          res = await clientYellowInstance.post(
            "/insert/yellow-consultant",
            consultant.value
          );
        } else {
          error.value = "Алдаа гарлаа.";
          setTimeout(() => {
            error.value = null;
          }, 5000);
        }

        if (res.status === 200) {
          success.value = true;
        }
      } catch (err) {
        error.value = "Алдаа гарлаа.";
        setTimeout(() => {
          error.value = null;
        }, 5000);
      } finally {
        isLoading.value = false;
      }
    };

    return {
      lang,
      success,
      error,
      isLoading,
      steps,
      currentStep,
      registrant,
      stepOneRef,
      addStep,
      backToPreviousStep,
      selectedType,
      selectType,
      company,
      consultant,
      companyRef,
      consultantRef,
      stepThreeRef,
      submitRequest,
      goBackToSpecificStep,
      startAgain,
    };
  },
};
</script>

<style scoped>
.backgroundImage {
  background-image: url("../../assets/images/YellowPage/mining-industry.jpg");
}
</style>
